import React, { useState, useRef, useEffect } from "react";
import { Search, X, Loader } from "lucide-react";
import { showError, showSuccess } from "@/utils/toast";

interface Product {
    id: number;
    name: string;
    category_id: number;
    category: string;
    brand_id: number | null;
    brand_name: string | null;
    ean_code: string | null;
    base_shop_id?: number | null;
    base_shop_name?: string | null;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
}

interface ExtPromotion {
    id: number;
    product_data: {
        name: string;
        category_code?: string;
        brand_name?: string;
    };
    promotion_data: {
        promotion_type?: string;
        value?: number;
        currency?: string;
        discount_percentage?: number;
        original_price?: number;
        discounted_price?: number;
        starts_at?: string | number;
        ends_at?: string | number;
    };
    packaging_data: {
        name?: string;
        size_value?: string;
        size_unit?: string;
        multiplier?: number;
    };
    prices_data: Array<{
        price_type_code: string;
        value: number;
        starts_at?: string;
        ends_at?: string;
        description?: string;
    }>;
    deposit_data?: {
        deposit_type?: string;
        price?: number;
        currency?: string;
    };
    existing_product?: {
        id: number;
        name: string;
        brand_name?: string;
        base_shop_name?: string;
        category?: string;
    } | null;
}

interface ExtActionManagerProps {
    onActionSelected?: (action: ExtPromotion, product: Product) => void;
    onCancel?: () => void;
}

// STYLES
const boxStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 200,
    borderRadius: 6,
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.04)",
    position: "relative",
    padding: "12px",
    boxSizing: "border-box",
    gap: "8px",
    overflow: "auto",
};

const labelStyle: React.CSSProperties = {
    color: "#ddd",
    fontSize: 13,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.5
};

const inputContainerStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    alignItems: "center",
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    color: "#fff",
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
};

const searchIconStyle: React.CSSProperties = {
    position: "absolute",
    right: 8,
    color: "#666",
    width: 16,
    height: 16,
};

const dropdownStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#1a1a1a",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    maxHeight: 200,
    overflowY: "auto",
    zIndex: 100,
    marginTop: 2,
};

const dropdownItemStyle: React.CSSProperties = {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
    fontSize: 14,
    color: "#ddd",
    transition: "background-color 0.2s",
};

const selectedItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    borderRadius: 4,
    fontSize: 14,
    color: "#fff",
    marginBottom: 8,
};

const messageStyle: React.CSSProperties = {
    padding: "8px 12px",
    backgroundColor: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    fontSize: 13,
    color: "#999",
    textAlign: "center",
};

const submitButtonStyle: React.CSSProperties = {
    padding: "10px 16px",
    backgroundColor: "rgba(34, 197, 94, 0.3)",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    borderRadius: 4,
    color: "#22c55e",
    cursor: "pointer",
    fontSize: 15,
    fontWeight: "bold",
    transition: "all 0.2s",
};

export default function ExtActionManager({
    onActionSelected,
    onCancel,
}: ExtActionManagerProps) {
    // TERMÉK KERESÉS
    const [productQuery, setProductQuery] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showProductDropdown, setShowProductDropdown] = useState(false);

    // AKCIÓ KERESÉS
    const [availableActions, setAvailableActions] = useState<ExtPromotion[]>([]);
    const [selectedAction, setSelectedAction] = useState<ExtPromotion | null>(null);
    const [loadingActions, setLoadingActions] = useState(false);

    // REFERENCIÁK
    const productInputRef = useRef<HTMLInputElement>(null);

    // TERMÉK KERESÉS API
    useEffect(() => {
        if (productQuery.length < 2) {
            setProducts([]);
            setShowProductDropdown(false);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`/api/admin/products/search_by_name?q=${encodeURIComponent(productQuery)}`);
                if (response.ok) {
                    const data = await response.json();
                    setProducts(data.products || []);
                    if (data.products?.length > 0) {
                        setShowProductDropdown(true);
                    }
                }
            } catch (error) {
                console.error("Hiba a termékek kereséskor:", error);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [productQuery]);

    // TERMÉK KIVÁLASZTÁSA ÉS AKCIÓK BETÖLTÉSE
    const handleProductSelect = async (product: Product) => {
        setSelectedProduct(product);
        setProductQuery("");
        setShowProductDropdown(false);
        setSelectedAction(null);

        // Akciók keresése a termék alapján
        await loadActionsForProduct(product.id);
    };

    const loadActionsForProduct = async (productId: number) => {
        try {
            setLoadingActions(true);
            const response = await fetch(`/api/admin/ext-promotion-stagings?product_id=${productId}`);
            if (response.ok) {
                const data = await response.json();
                const stagings = Array.isArray(data) ? data : data.data || [];
                setAvailableActions(stagings);
                if (stagings.length === 0) {
                    showError("Ez a termékhez nincs elérhető akció a staging adatbázisban.");
                }
            } else {
                showError("Hiba az akciók betöltéskor.");
            }
        } catch (error) {
            console.error("Hiba az akciók betöltéskor:", error);
            showError("Hiba az akciók betöltéskor.");
        } finally {
            setLoadingActions(false);
        }
    };

    const handleRemoveProduct = () => {
        setSelectedProduct(null);
        setProductQuery("");
        setAvailableActions([]);
        setSelectedAction(null);
    };

    const handleLoadAction = () => {
        if (selectedProduct && selectedAction) {
            onActionSelected?.(selectedAction, selectedProduct);
            showSuccess("Akció sikeresen betöltve!");
        }
    };

    const handleCancel = () => {
        setSelectedProduct(null);
        setProductQuery("");
        setAvailableActions([]);
        setSelectedAction(null);
        onCancel?.();
    };

    return (
        <div style={boxStyle}>
            <div style={labelStyle}>🔗 Ext-Akció-Kezelő</div>

            {!selectedProduct ? (
                <>
                    {/* TERMÉK KERESÉS */}
                    <div style={{ position: "relative" }}>
                        <div style={inputContainerStyle}>
                            <input
                                ref={productInputRef}
                                type="text"
                                placeholder="Termék neve (pl. Szőlő)..."
                                value={productQuery}
                                onChange={(e) => setProductQuery(e.target.value)}
                                onFocus={() => products.length > 0 && setShowProductDropdown(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setShowProductDropdown(false);
                                        setProductQuery("");
                                    }
                                }}
                                style={inputStyle}
                            />
                            <Search style={searchIconStyle} size={16} />
                        </div>

                        {showProductDropdown && products.length > 0 && (
                            <div style={dropdownStyle}>
                                {products.map((product) => (
                                    <div
                                        key={product.id}
                                        style={{ ...dropdownItemStyle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "transparent";
                                        }}
                                    >
                                        <div
                                            style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, cursor: "pointer" }}
                                            onClick={() => handleProductSelect(product)}
                                        >
                                            <div style={{ fontWeight: 500 }}>{product.name}</div>
                                            <div style={{ fontSize: 11, color: "#aaa", display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                {product.brand_name && (
                                                    <span style={{ backgroundColor: "rgba(59, 130, 246, 0.1)", padding: "2px 6px", borderRadius: "3px" }}>
                                                        🏷️ {product.brand_name}
                                                    </span>
                                                )}
                                                <span>📂 {product.category}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {productQuery.length >= 2 && products.length === 0 && (
                        <div style={messageStyle}>
                            ℹ️ Nincs találat a keresésre
                        </div>
                    )}

                    {productQuery.length < 2 && (
                        <div style={messageStyle}>
                            📝 Legalább 2 karaktert írjon a kereséshez
                        </div>
                    )}
                </>
            ) : (
                <>
                    {/* KIVÁLASZTOTT TERMÉK */}
                    <div style={selectedItemStyle}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{selectedProduct.name}</div>
                            <div style={{ fontSize: 11, color: "#aaa", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                {selectedProduct.brand_name && (
                                    <span style={{ backgroundColor: "rgba(59, 130, 246, 0.1)", padding: "2px 6px", borderRadius: "3px", fontWeight: 500 }}>
                                        🏷️ {selectedProduct.brand_name}
                                    </span>
                                )}
                                {selectedProduct.category && (
                                    <span style={{ backgroundColor: "rgba(168, 85, 247, 0.1)", padding: "2px 6px", borderRadius: "3px" }}>
                                        📂 {selectedProduct.category}
                                    </span>
                                )}
                            </div>
                        </div>
                        <X size={16} onClick={handleRemoveProduct} style={{ cursor: "pointer", flexShrink: 0, marginTop: 2 }} />
                    </div>

                    {/* AKCIÓK BETÖLTÉSE */}
                    {loadingActions && (
                        <div style={messageStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                                <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
                                Akciók keresése...
                            </div>
                        </div>
                    )}

                    {!loadingActions && availableActions.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <div style={{ ...labelStyle, marginBottom: 4 }}>
                                Elérhető akciók ({availableActions.length}):
                            </div>

                            {availableActions.map((action) => (
                                <div
                                    key={action.id}
                                    onClick={() => setSelectedAction(action)}
                                    style={{
                                        padding: "10px 12px",
                                        backgroundColor: selectedAction?.id === action.id
                                            ? "rgba(34, 197, 94, 0.2)"
                                            : "rgba(255,255,255,0.05)",
                                        border: selectedAction?.id === action.id
                                            ? "1px solid rgba(34, 197, 94, 0.4)"
                                            : "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedAction?.id !== action.id) {
                                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedAction?.id !== action.id) {
                                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                                        }
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            border: "2px solid",
                                            borderColor: selectedAction?.id === action.id ? "#22c55e" : "rgba(255,255,255,0.3)",
                                            backgroundColor: selectedAction?.id === action.id ? "#22c55e" : "transparent",
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>
                                                💰 {action.promotion_data.promotion_type || "Akció"}
                                            </div>
                                            <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                                                {action.promotion_data.original_price && (
                                                    <span>Eredeti: {action.promotion_data.original_price} {action.promotion_data.currency} | </span>
                                                )}
                                                {action.promotion_data.discounted_price && (
                                                    <span>Akciós: {action.promotion_data.discounted_price} {action.promotion_data.currency}</span>
                                                )}
                                                {action.promotion_data.discount_percentage && (
                                                    <span>{action.promotion_data.discount_percentage}% kedvezmény</span>
                                                )}
                                            </div>
                                            {(action.promotion_data.starts_at || action.promotion_data.ends_at) && (
                                                <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                                                    ⏰ {action.promotion_data.starts_at} → {action.promotion_data.ends_at}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loadingActions && availableActions.length === 0 && (
                        <div style={messageStyle}>
                            ℹ️ Nincs elérhető akció ehhez a termékhez
                        </div>
                    )}

                    {/* GOMBOK */}
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button
                            onClick={handleLoadAction}
                            disabled={!selectedAction || loadingActions}
                            style={{
                                ...submitButtonStyle,
                                flex: 1,
                                opacity: (!selectedAction || loadingActions) ? 0.5 : 1,
                                cursor: (!selectedAction || loadingActions) ? "not-allowed" : "pointer"
                            }}
                            onMouseEnter={(e) => {
                                if (selectedAction && !loadingActions) {
                                    e.currentTarget.style.backgroundColor = "rgba(34, 197, 94, 0.5)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedAction && !loadingActions) {
                                    e.currentTarget.style.backgroundColor = "rgba(34, 197, 94, 0.3)";
                                }
                            }}
                        >
                            📥 Akció betöltése
                        </button>

                        <button
                            onClick={handleCancel}
                            style={{
                                ...submitButtonStyle,
                                flex: 1,
                                backgroundColor: "rgba(239, 68, 68, 0.2)",
                                borderColor: "rgba(239, 68, 68, 0.3)",
                                color: "#ef4444",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.4)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.2)";
                            }}
                        >
                            ← Vissza
                        </button>
                    </div>
                </>
            )}

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
